import { FC, useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'

type Props = {
    monthlyTrend: Array<{ _id: { year: number; month: number }; count: number }>
}

export const ProjectTrendChart: FC<Props> = ({ monthlyTrend }) => {
    const chartRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!chartRef.current || monthlyTrend.length === 0) {
            return
        }

        const { categories, data } = prepareChartData(monthlyTrend)
        const chart = new ApexCharts(chartRef.current, getChartOptions(categories, data))
        chart.render()

        return () => {
            chart.destroy()
        }
    }, [monthlyTrend])

    const prepareChartData = (
        trend: Array<{ _id: { year: number; month: number }; count: number }>
    ) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const categories = trend.map((t) => `${months[t._id.month - 1]} ${t._id.year}`)
        const data = trend.map((t) => t.count)
        return { categories, data }
    }

    if (monthlyTrend.length === 0) {
        return (
            <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                No trend data available
            </div>
        )
    }

    return <div ref={chartRef} style={{ height: '280px' }}></div>
}

function getChartOptions(categories: string[], data: number[]): ApexOptions {
    return {
        series: [{ name: 'Projects', data }],
        chart: {
            fontFamily: "'Hanken Grotesk', sans-serif",
            type: 'area',
            height: 280,
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: { enabled: true, speed: 700 },
        },
        dataLabels: { enabled: false },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.34,
                opacityTo: 0.02,
                stops: [0, 92, 100],
            },
        },
        stroke: {
            curve: 'smooth',
            width: 3,
            colors: ['#d97706'],
        },
        xaxis: {
            categories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                style: {
                    colors: '#64748b',
                    fontSize: '12px',
                    fontFamily: "'Hanken Grotesk', sans-serif",
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#94a3b8',
                    fontSize: '12px',
                    fontFamily: "'Archivo', sans-serif",
                },
            },
        },
        colors: ['#d97706'],
        grid: {
            borderColor: '#eef1f5',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { left: 8, right: 12 },
        },
        tooltip: {
            style: {
                fontSize: '13px',
                fontFamily: "'Hanken Grotesk', sans-serif",
            },
            y: {
                formatter: (val) => `${val} projects`,
            },
            marker: { show: true },
        },
        markers: {
            size: 0,
            hover: { size: 6 },
            strokeColors: '#d97706',
            strokeWidth: 2,
            colors: ['#ffffff'],
        },
    }
}
